import { useMemo, useRef } from 'react';

type SlidingDelayOptions<Suppress extends boolean = false> = {
  /** If `true`, rejects all pending tasks with {@link SlidingDelayError} with `'PENDING_REJECTED'` code, if any. Default: `false` */
  rejectPending?: boolean;

  /** If `true`, aborts the currently running task, if any. The running task is rejected with {@link SlidingDelayError} with `'RUNNING_ABORTED'` code. Default: `false` */
  abortRunning?: boolean;

  /** Max period, in ms, to wait for aborted task to exit. If elapsed, {@link SlidingDelayError} with `'CANNOT_START'` code is raised. Default is 2,000 ms. */
  maxWaitAborted?: number;

  /**
   * Instructs the debounced function not to propagate rejections outwards, both from the worker `fn` and those fired with the options above.
   * If `true`, the resulting promise is resolved with `undefined`.
   * Setting `suppressRejections` to `true` makes sense when the call to the useSlidingDelay's result function is not placed in a try/catch block so that the rejections are not left unhandled.
   */
  suppressRejections?: Suppress;
};

export type SlidingDelayFunction<F extends AnyFunc, Suppress extends boolean = false> = F extends (
  ...args: any[]
) => any
  ? ((...arg: ProxyParams<F>) => Promise<Result<F, Suppress>>) & {
      /** Gets the count of all pending fn runs */
      readonly pendingCount: number;

      /** Gets if a task is currently running */
      readonly isRunning: boolean;

      /** Cancels all pending fn runs, if any */
      cancel: () => void;

      /** Rejects all pending fn runs, if any, with {@link SlidingDelayError} with `'PENDING_REJECTED'` code */
      reject: () => void;

      /** Runs the function immediately and resolves promises of all the previously enqueued calls, if any, with the result */
      immediate: (...args: ProxyParams<F>) => Promise<Result<F, Suppress>>;
    }
  : never;

/**
 * Represents return type of the debounced function.
 * Depending on `suppressRejections` option, it may or may not return `undefined`, which is the case when an error is suppressed.
 */
export type Result<F extends AnyFunc, Suppress extends boolean = false> = Suppress extends true
  ? Awaited<ReturnType<F>> | undefined
  : Awaited<ReturnType<F>>;

/** Internal enqueued task representation */
type Pending<F extends AnyFunc> = {
  timer: ReturnType<typeof setTimeout>;
  resolve: (value: Awaited<ReturnType<F>>) => void;
  reject: (err: any) => void;
};

/** A function constraint shortcut */
export type AnyFunc = (...args: any[]) => any;

/**
 * Passed as a last addendum argument to `fn` to control the flow from inside
 * and react to outer rejections.
 */
export type OnCallOptions = {
  /** Signals when the task is being aborted from outside, f.x. when the ongoing task is canceled when the next one starts. */
  readonly signal: AbortSignal;
};

/**
 * Resolves the resulting deferred function parameters:
 *   * If all parameters are not optional,
 *   * And the last parameter of `fn` is {@link OnCallOptions}, trims the last parameter;
 *   * Otherwise, returns the input parameter array.
 *
 * @example
 * type PF = (a: number, d: Date, options: OnCallOptions) => Promise<number>;
 * type PFA = ProxyParams<PF>; // [a: number, d: Date] -- note the options arg is trimmed
 */
type ProxyParams<F extends AnyFunc> = F extends (...args: infer P) => any
  ? P extends [...infer Rest, infer Last]
    ? Last extends OnCallOptions
      ? Rest extends Required<Rest>
        ? Required<Rest>
        : P
      : P
    : P
  : never;

const cancelErrorMarker = Symbol('SlidingDelayError');
const errorCodes = ['PENDING_REJECTED', 'RUNNING_ABORTED', 'CANNOT_START', 'SLEEP_REJECTED'] as const;
type SlidingDelayErrorCode = (typeof errorCodes)[number];

export class SlidingDelayError extends Error {
  static CODES = errorCodes;

  constructor(
    message: string,
    public readonly code: SlidingDelayErrorCode
  ) {
    super(message);
  }
  readonly [cancelErrorMarker] = true;

  static isInstance(err: unknown): err is SlidingDelayError {
    return !!(err as SlidingDelayError)?.[cancelErrorMarker];
  }

  static isCode(err: unknown, code: SlidingDelayErrorCode): boolean {
    return SlidingDelayError.isInstance(err) && err.code === code;
  }
}

export type Deferred<T> = {
  resolve: (value: T) => void;
  reject: (reason: any) => void;
  promise: Promise<T>;
};

/** Creates a `Promise` of the specified type and returns it along with its `resolve` and `reject` callbacks. */
export const createDeferred = <T>(): Deferred<T> => {
  let resolve: (value: T) => void;
  let reject: (reason: any) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { resolve: resolve!, reject: reject!, promise };
};

type SleepPromise = Promise<void> & { cancel: () => void };

/** Returns a cancellable `Promise` that is resolved with a void value after the specified delay, in ms. */
export const sleep = (delay: number = 0): SleepPromise => {
  const { resolve, reject, promise } = createDeferred<void>();
  const timeout = setTimeout(() => resolve(), delay);
  Object.assign(promise, {
    cancel: (shouldReject: boolean = false) => {
      clearTimeout(timeout);
      if (shouldReject) reject(new SlidingDelayError('Sleep delay rejected', 'SLEEP_REJECTED'));
    },
  });
  return promise as SleepPromise;
};

/**
 * Returns `true` if the the `fn` **presumably** expects {@link OnCallOptions} as its last argument.
 */
const isFnWithOptions = <F extends AnyFunc>(fn: F, args: unknown) =>
  typeof fn === 'function' && Array.isArray(args) && fn.length === args.length + 1;

const sleepFrame = 10;
const defaultMaxWaitAborted = 2_000;

/**
 * Returns a function with the same signature as `fn`.
 * When the function is called,
 *   * the `fn` call is enqueued with the `wait` delay,
 *   * cancels previously enqueued calls, if any.
 *
 * Hence, it basically works as lodash `debounce` with one specific behavior:
 * the resulting promises of all calls are resolved with the same result of the last debounced call.
 *
 * See usage examples in the tests nearby {@link file://./useSlidingDelay.test.ts}
 *
 * @param fn A function to debounce. It is meaningful if it is async.
 * @param wait Time to delay the call, in ms.
 * @param rejectPending If `true`, cancels all pending fn runs, if any. Default: `false`.
 * @param abortRunning If `true`, aborts the currently running task, if any. Default: `false`.
 * @param maxWaitAborted Max period, in ms, to wait for aborted task to exit. If elapsed, {@link SlidingDelayError} with `'CANNOT_START'` code is raised. Default is 2,000 ms.
 */
export const useSlidingDelay = <F extends AnyFunc, Suppress extends boolean = false>(
  fn: F,
  wait: number = 0,
  {
    rejectPending: optRejectPending,
    abortRunning: optAbortRunning,
    suppressRejections: optSuppressRejections = false as Suppress,
    maxWaitAborted: optMaxWaitAborted = defaultMaxWaitAborted,
  }: SlidingDelayOptions<Suppress> = {}
): SlidingDelayFunction<F> => {
  if (optAbortRunning === true && optRejectPending === false) {
    throw new Error('When abortRunning is true, cancelPending cannot be false');
  }

  const volatile = useRef({
    fn,
    prev: null as null | SlidingDelayFunction<F>,
    pending: null as null | Set<Pending<F>>,
    abortRunning: null as null | AnyFunc,
  });

  // this mutable reference ensures that the latest instance of the passed fn is called
  volatile.current.fn = fn;

  return useMemo<SlidingDelayFunction<F>>(() => {
    type _Result = Awaited<ReturnType<F>>;
    let { pending } = volatile.current;

    const cancelPending = () => pending!.forEach(({ timer }) => clearTimeout(timer));

    const resolvePending = (value: _Result) => pending!.forEach(({ resolve }) => resolve(value));

    const rejectPending = (reason: any = new SlidingDelayError('The pending task is rejected', 'PENDING_REJECTED')) =>
      pending!.forEach(({ reject }) => reject(reason));

    // when recreating (f.x. on options change), and there are pending tasks, reject them according to the rejectPending option
    if (optRejectPending && pending) rejectPending();
    volatile.current.pending = pending = new Set();

    /** essentially, runs the task fn */
    const run = async (args: ProxyParams<F>, reject?: (reason: any) => void): Promise<Result<F, Suppress>> => {
      const aborter = new AbortController();

      await waitAborted();

      volatile.current.abortRunning = (reason: any = new SlidingDelayError('Task is aborted', 'RUNNING_ABORTED')) => {
        if (aborter.signal.aborted) return;
        aborter.abort(reason);
        reject?.(reason);
      };

      try {
        const callArgs = isFnWithOptions(volatile.current.fn, args)
          ? [...args, { signal: aborter.signal } satisfies OnCallOptions]
          : args;
        const result = await volatile.current.fn?.(...callArgs);

        // some routines might not subscribe/react to the abort events, therefore, we throw the passed abortion reason explicitly
        if (aborter.signal.reason) throw aborter.signal.reason;
        // otherwise, resolve with the result successfully
        resolvePending(result);
        return result;
      } catch (err) {
        // if it is an abortion, it is not this task's responsibility to reject others
        if (!aborter.signal.reason) rejectPending(err);
        if (optSuppressRejections) return undefined as Result<F, Suppress>;

        // WARNING: this error can go higher the stack and lst unhandled.
        // If the call to useSlidingDelay's result function is not placed in a try/catch block, it makes sense to suppressRejections option to true.
        throw err;
      } finally {
        volatile.current.abortRunning = null;
        await Promise.resolve(); // flush pending, if using fake test timers
      }
    };

    const checkPending = () => {
      //   1. cancel previous timers to debounce
      cancelPending();
      //   2. according to cancelPending and abortRunning option, reject all pending tasks, if any
      if (optRejectPending || optAbortRunning) rejectPending();
      //   3. according to the abortRunning option, abort the running task, if any
      if (optAbortRunning) volatile.current.abortRunning?.();
    };

    const waitAborted = async () => {
      // no currently running task OR no need to wait for it to exit
      if (!optAbortRunning || optMaxWaitAborted === 0) return;

      let elapsed = 0;
      while (volatile.current.abortRunning) {
        if (elapsed >= optMaxWaitAborted)
          throw new SlidingDelayError('Cannot start the task because the previous one could exit', 'CANNOT_START');
        await sleep(sleepFrame);
        elapsed += sleepFrame;
        await Promise.resolve();
      }
    };

    /** Resulting function that is returned by useSlidingDelay. See {@link SlidingDelayFunction} */
    const proxy = (async (...args: ProxyParams<F>) => {
      // here we are going to enqueue a new task
      checkPending();

      // the new task's result promise
      let { promise, resolve, reject } = createDeferred<Promise<Result<F, Suppress>>>();

      const entry: Pending<F> = {
        resolve,
        reject,
        timer: setTimeout(async () => {
          // the task starts running, it is not pending anymore
          pending.delete(entry);
          try {
            const result = await run(args, reject);
            resolve(result as Promise<Result<F, Suppress>>);
            await Promise.resolve();
          } catch (err) {
            reject(err);
          }
        }, wait),
      };

      pending.add(entry);

      // sorry guys, the task promise is resolved from outside, therefore no way to use try-await-finally
      if (optSuppressRejections) promise = promise.catch((_reason) => undefined as _Result);

      return promise.finally(() => pending.delete(entry));
    }) as SlidingDelayFunction<F>;

    proxy.immediate = async (...args: ProxyParams<F>): Promise<Result<F, Suppress>> => {
      await checkPending();
      return run(args);
    };

    proxy.cancel = cancelPending;
    proxy.reject = rejectPending;

    Object.defineProperty(proxy, 'pendingCount', { enumerable: true, configurable: true, get: () => pending.size });
    Object.defineProperty(proxy, 'isRunning', {
      enumerable: true,
      configurable: true,
      get: () => !!volatile.current.abortRunning,
    });

    // on re-init, when deps are changed, cancel pending, if any
    volatile.current.prev?.cancel();
    volatile.current.prev = proxy;

    return proxy;
  }, [optRejectPending, optAbortRunning, optMaxWaitAborted, optSuppressRejections, wait]);
};

if (typeof setImmediate === 'undefined') {
  global.setImmediate = setTimeout as unknown as typeof setImmediate;
}
